/**
 * Flexible Closet Design Quiz
 * Handles the interactive quiz form functionality including:
 * - Multi-step form navigation
 * - Option selection and branching logic
 * - Form validation and submission
 * - UTM and tracking parameter handling
 */

document.addEventListener('DOMContentLoaded', function () {
    // =========================================
    // Initial Setup and Validation
    // =========================================
    const form = document.querySelector('.quiz-form');
    if (!form) {
        console.error('Quiz form not found');
        return;
    }

    const pages = document.querySelectorAll('.quiz-page');
    if (pages.length === 0) {
        console.error('No form pages found');
        return;
    }

    let selectedPipeline = 'Retail'; // Default to Retail

    // =========================================
    // Core Form Functions
    // =========================================

    /**
     * Initializes the form by showing only the first step
     */
    function initializePages() {
        pages.forEach((page, index) => {
            page.style.display = index === 0 ? 'block' : 'none';
            if (index === 0) {
                page.classList.add('in-wizard')
            }
        });
    }

    /**
     * Handles the selection of options in the form
     * @param {HTMLElement} selectedElement - The element that was selected
     * @param {HTMLElement} optionGroup - The group containing the options
     */
    function handleOptionSelection(selectedElement, optionGroup) {
        optionGroup.querySelectorAll('.option-label').forEach(label => {
            label.classList.remove('selected');
        });
        selectedElement.classList.add('selected');
    }

    /**
     * Handles the branching logic for form navigation
     * @param {string} targetStep - The step to navigate to
     * @param {string} targetUrl - The URL to redirect to (if any)
     */
    function handleBranching(targetStep, targetUrl) {
        if (targetUrl) {
            window.location.href = targetUrl;
            return;
        }

        if (targetStep) {
            pages.forEach(page => {
                page.style.display = 'none';
            });

            const targetStepElement = document.querySelector(`.quiz-page[data-page="${targetStep}"]`);
            if (targetStepElement) {
                targetStepElement.style.display = 'block';
                setWizard(targetStepElement)
            }
        }
    }

    /**
     * Handles navigation between form steps
     * @param {HTMLElement} currentStep - The current step element
     * @param {string} skipToStep - Optional step ID to skip to
     * @param {boolean} previous - Go to previous step
     */
    function navigateToStep(currentStep, skipToStep, previous = false) {
        if (skipToStep) {
            // Skip to specified step
            const targetStep = document.querySelector(`.quiz-page[data-page="${skipToStep}"]`);
            if (targetStep) {
                currentStep.style.display = 'none';
                targetStep.style.display = 'block';
                setWizard(targetStep)
            }
        } else {
            // Default behavior - go to next step
            let nextStepSection;
            if (previous) {
                currentStep.classList.remove('in-wizard')
                currentStep.querySelectorAll('input,textarea,select').forEach(input => {
                    input.disabled = true;
                });

                const wizardPages = document.querySelectorAll('.quiz-page.in-wizard');
                if (wizardPages.length) {
                    nextStepSection = wizardPages[wizardPages.length - 1].parentElement;
                } else {
                    nextStepSection = document.querySelector('.quiz-page').parentElement;
                }
            } else {
                nextStepSection = currentStep.parentElement.nextElementSibling;
            }
            const nextStep = nextStepSection?.querySelector(".quiz-page")
            if (nextStep) {
                currentStep.style.display = 'none';
                nextStep.style.display = 'block';
                setWizard(nextStep)
            }
        }
    }



    // Update progress bars and groups appropriately
    function initProgressBar() {
        const track = document.querySelector('.flexible-cdq-progress-track');
        if (!track) return;

        const allGroups = [...pages].map(p => parseInt(p.dataset.group, 10)).filter(n => !isNaN(n));
        const totalGroups = Math.max(...allGroups);

        track.innerHTML = '';
        for (let i = 1; i <= totalGroups; i++) {
            const seg = document.createElement('div');
            seg.className = 'flexible-cdq-progress-segment';
            seg.dataset.group = i;
            track.appendChild(seg);
        }
    }

    function updateProgressBar(step) {
        const currentGroup = parseInt(step.dataset.group, 10);
        document.querySelectorAll('.flexible-cdq-progress-segment').forEach(seg => {
            const g = parseInt(seg.dataset.group, 10);
            seg.classList.toggle('filled', g < currentGroup);
        });
    }

    /**
     * Set up footer bar
     * @param {HTMLElement} step - The current step element
     */

    function setWizard(step) {
        const groupId = step.dataset.group
        const page = step.dataset.page
        step.classList.add('in-wizard')

        // Enable fields in step
        step.querySelectorAll(':disabled').forEach(input => {
            input.disabled = false;
        });
        step.querySelectorAll('.changed').forEach(input => {
            input.classList.remove('changed')
        });

        document.querySelectorAll(`.flexible-cdq-bar-group .img`).forEach(imgDiv => {
            imgDiv.classList.remove('active')
        });
        const footerGroup = document.querySelector(`.flexible-cdq-bar-group[data-group="${groupId}"]`)
        if (footerGroup) {
            footerGroup.querySelectorAll(".img").forEach(imgDiv => {
                if (imgDiv.dataset.steps.split(',').includes(page)) {
                    imgDiv.classList.add('active')
                }
            });
        }

        // Update progress bars and groups appropriately
        updateProgressBar(step);

        // Set navigation arrows
        const prevArrow = document.querySelector('.prev-arrow');
        if (prevArrow) {
            const isFirstPage = step.dataset.page === '1';
            prevArrow.style.display = isFirstPage ? 'none' : '';
        }

        // Evaluate button state for the new step
        updateButtonState(step);

        // filter fields on multiple room selection page
        filterRoomTypeFields(step);

        // scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // =========================================
    // Button Type Logic
    // =========================================

    /**
     * Returns true if the page has at least one filled/selected field.
     * Used to decide whether mandatory/submit buttons should be enabled,
     * and whether the optional button should say "Continue" or "I'm not sure, let's skip it".
     * @param {HTMLElement} page
     * @returns {boolean}
     */
    function pageHasInput(page) {
        // Any checked radio or checkbox
        if (page.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) {
            return true;
        }
        // Any non-empty text/email/tel/number/textarea
        for (const field of page.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="number"], textarea')) {
            if (field.value.trim() !== '') return true;
        }
        // Any file selected
        for (const field of page.querySelectorAll('input[type="file"]')) {
            if (field.files && field.files.length > 0) return true;
        }
        return false;
    }

    /**
 * Returns true when every required text/email/tel/number/textarea on the page is filled.
 * Used for the submit button type.
 * @param {HTMLElement} page
 * @returns {boolean}
 */
    function allTextFieldsFilled(page) {
        const requiredFields = page.querySelectorAll(
            'input[type="text"][required], input[type="email"][required], input[type="tel"][required], input[type="number"][required], textarea[required]'
        );
        for (const field of requiredFields) {
            if (field.value.trim() === '') return false;
        }
        return true;
    }

    /**
     * Updates the continue/submit button state and label for a given page
     * based on its button type and current field state.
     *
     * btn-type-mandatory: disabled until fields filled, label always "Continue"
     * btn-type-optional:  always enabled; label = "I'm not sure, let's skip it" when empty,
     *                     "Continue" when filled
     * btn-type-submit:    disabled until fields filled, label always "Submit"
     * btn-type-no_button: no button rendered, nothing to update
     *
     * @param {HTMLElement} page
     */
    function updateButtonState(page) {
        const btn = page.querySelector('.flexible-cdq-continue');
        if (!btn) return;

        const filled = pageHasInput(page);

        if (btn.classList.contains('btn-type-mandatory')) {
            btn.disabled = !filled;

            let hint = btn.nextElementSibling;
            if (!hint || !hint.classList.contains('btn-hint')) {
                hint = document.createElement('p');
                hint.className = 'btn-hint';
                btn.insertAdjacentElement('afterend', hint);
            }
            hint.textContent = 'Choose at least one to continue';
            hint.style.display = filled ? 'none' : 'block';

        } else if (btn.classList.contains('btn-type-optional')) {
            btn.disabled = false;
            btn.textContent = filled ? 'Continue' : "I'm not sure, let's skip it";
            if (!filled) btn.classList.add("skip-state");
            if (filled) btn.classList.remove("skip-state");

        } else if (btn.classList.contains('btn-type-submit')) {
            const isDesktop = window.matchMedia('(min-width: 768px)').matches;
            console.log(" isDesktop:", isDesktop, "forceEnabled:", btn.dataset.forceEnabled);
            if (isDesktop && btn.dataset.forceEnabled === 'true') {
                btn.disabled = false;
            } else {
                btn.disabled = !allTextFieldsFilled(page);
            }
        }
    }

    /**
     * Attach input/change listeners to all fields on a page so the button
     * state updates live as the user interacts.
     * Safe to call multiple times — uses a data attribute to avoid double-binding.
     * @param {HTMLElement} page
     */
    function bindButtonStateListeners(page) {
        if (page.dataset.buttonListenersBound) return;
        page.dataset.buttonListenersBound = 'true';

        page.querySelectorAll('input, textarea, select').forEach(field => {
            const eventType = (field.type === 'radio' || field.type === 'checkbox' || field.type === 'file')
                ? 'change'
                : 'input';
            field.addEventListener(eventType, () => updateButtonState(page));
        });
    }

    /**
   * Populate hidden field q55_ab_tests with Intelligems experiments/variants
   */
    function populateABTestsHiddenField() {
        const setField = (val) => {
            const fld = document.querySelector('input[name="q55_ab_tests"]');
            if (fld) fld.value = val || '';
        };

        // Default to empty if the field isn't present
        if (!document.querySelector('input[name="q55_ab_tests"]')) return;

        try {
            // 1. READ FROM LOCALSTORAGE HISTORY INSTEAD OF WINDOW.INTELLIGEMS
            const stored = localStorage.getItem('ig_historical_experiments');
            const experiments = stored ? JSON.parse(stored) : [];

            if (!experiments.length) {
                console.warn('[Form Populate] No historical experiments found in localStorage');
                setField('');
                return;
            }

            // 2. Loop through the combined, persistent experiments we saved earlier
            let abtData = '';
            experiments.forEach((experiment) => {
                try {
                    const expName = experiment.name || 'Unknown Experiment';
                    const vName = experiment.variant || 'unknown';
                    abtData += `${expName} : ${vName}; `;
                } catch (err) {
                    console.error('Error processing experiment from storage:', err);
                }
            });

            console.log('[Form Populate] Successfully injected into hidden field:', abtData.trim());
            setField(abtData.trim());
        } catch (error) {
            console.error('Error retrieving Intelligems experiments from storage:', error);
        }
    }


    /**
     * Copies all form fields from the quiz and submit form
     */
    function copyFieldsAndSubmitForm() {
        const mainForm = document.getElementById('flexible-closet-quiz-form');
        if (!mainForm) return;


        let emailField = document.querySelector(".quiz-page input[type=email]")
        let phoneField = document.querySelector(".quiz-page input[type=tel]")

        let productCategory = undefined;
        let referrer = (document.referrer ?? "").toLowerCase();
        const requestUuid = localStorage.getItem('request_uuid') ?? window.requestUuid;

        let requestIdField = mainForm.querySelector("input.request_id")
        if (requestIdField) {
            requestIdField.value = requestUuid
        }
        let vistorIdField = mainForm.querySelector("input.visitor_id")
        if (vistorIdField) {
            vistorIdField.value = localStorage.getItem('mc_visitor_id')
        }

        if (referrer.includes("vista")) {
            productCategory = "vista";
        } else if (referrer.includes("alto")) {
            productCategory = "alto";
        } else if (referrer.includes("wardrobe-closet")) {
            productCategory = "milano";
        }
        let productCategoryField = mainForm.querySelector("input.product_category")
        if (productCategory && productCategoryField) {
            productCategoryField.value = productCategory
        }

        let utmContentField = mainForm.querySelector("input.utm_content")
        if (utmContentField && referrer.includes("pages/affordable-custom-closets-2")) {
            utmContentField.value = "matisyahu_facebook_campaign"
        }

        // get utm data from local storage
        var utmDataJson = localStorage.getItem('utmData');
        var utmData = utmDataJson && utmDataJson.length > 20 ? JSON.parse(utmDataJson) : (window.bestUtmData || {});

        // Add all parameters to hidden fields
        Object.entries(utmData).forEach(([key, value]) => {
            if (value) {
                const hiddenField = document.querySelector(`input.${key}`);
                if (hiddenField) {
                    hiddenField.value = value;
                }
            }
        });

        (function TP() {
            const email = emailField?.value;
            const phone = phoneField?.value;
            if (!email || !window.TriplePixel) {
                setTimeout(TP, 400);
                return;
            }
            TriplePixel('custom', 'Contact', {
                'email': email,
                'phone': phone
            });
        })();

        // Get all input fields from current page
        const fields = document.querySelectorAll('.quiz-page input,.quiz-page textarea,.quiz-page select');

        fields.forEach(field => {
            const fieldName = field.name;
            if (!fieldName) return;

            // Check if field already exists in main form
            let mainFormField = mainForm.querySelector(`[name="${fieldName}"]`);

            if (field.type === 'radio') {
                // For radio buttons, only copy if checked
                if (field.checked) {
                    if (!mainFormField) {
                        mainFormField = document.createElement('input');
                        mainFormField.type = 'hidden';
                        mainFormField.name = fieldName;
                        mainFormField.value = field.value;
                        mainForm.appendChild(mainFormField);
                    }
                }
            } else if (field.type === 'checkbox') {
                // For checkboxes, handle multiple values
                if (field.checked) {
                    mainFormField = document.createElement('input');
                    mainFormField.type = 'hidden';
                    mainFormField.name = fieldName;
                    mainFormField.value = field.value;
                    mainForm.appendChild(mainFormField);
                }
            } else if (field.type === 'file') {
                // For files, skip, sending as s3 url
            } else {
                // For other input types, copy value directly
                if (!mainFormField) {
                    mainFormField = document.createElement('input');
                    mainFormField.type = 'hidden';
                    mainFormField.name = fieldName;
                    mainFormField.value = field.value;
                    mainForm.appendChild(mainFormField);
                } else if (field.value && !mainFormField.value) {
                    mainFormField.value = field.value
                }
            }
        });

                // Single-room leads: copy q30_wallNotes into that room's wallNotes_* field
        const roomNoteFields = {
            'Master Bedroom': 'wallNotes_master-br',
            'Other Bedroom': 'wallNotes_other-br',
            'Pantry': 'wallNotes_pantry',
            'Laundry Room': 'wallNotes_laundry-room',
            'Mudroom': 'wallNotes_mudroom',
            'Garage': 'wallNotes_garage',
            'Media Center': 'wallNotes_media-center',
            'Something Else': 'wallNotes_something-else'
        };
        const checkedRooms = document.querySelectorAll('input[name="room_type"]:checked');
        const singleRoomNotes = mainForm.querySelector('[name="q30_wallNotes"]')?.value.trim();

        if (checkedRooms.length === 1 && singleRoomNotes) {
            const targetName = roomNoteFields[checkedRooms[0].value];
            let roomField = targetName && mainForm.querySelector(`[name="${targetName}"]`);
            if (targetName && !roomField) {
                roomField = document.createElement('input');
                roomField.type = 'hidden';
                roomField.name = targetName;
                mainForm.appendChild(roomField);
            }
            if (roomField && !roomField.value) roomField.value = singleRoomNotes;
        }

        addTrackingParameters();
        populateABTestsHiddenField();

        // Re-sync recaptcha score from the page that actually ran validation
        const activeQuizPage = document.querySelector('.quiz-page[style="display: block;"]');
        const currentRecaptchaScore = activeQuizPage?.querySelector('#recaptcha-score, input[name="recaptcha_score"]');
        if (currentRecaptchaScore) {
            const mainRecaptchaField = mainForm.querySelector(`[name="${currentRecaptchaScore.name}"]`);
            if (mainRecaptchaField) {
                mainRecaptchaField.value = currentRecaptchaScore.value;
            }
        }

        // Set recipe_link field
        const recipeLink = getRecipeLink(new FormData(mainForm));
        console.log(" recipeLink:", recipeLink);
        const recipeLinkField = mainForm.querySelector('input.recipe_link');
        console.log(" recipeLinkField:", recipeLinkField);
        if (recipeLinkField) {
            recipeLinkField.value = recipeLink;
        }

        // set mc_engaged_user field to populate from cookie 'mc_engaged_user' if exists, otherwise set to empty string
        const engagedUserField = mainForm.querySelector('input.mc_engaged_user');
        if (engagedUserField) {
            engagedUserField.value = getCookie('mc_engaged_user') || '';
        }

        // Browser timeclock (testing alongside zip code)
        const timeclockField = mainForm.querySelector('input.browser_timeclock');
        if (timeclockField) {
            try {
                timeclockField.value = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
            } catch (e) {
                timeclockField.value = '';
            }
        }

        // add to local storage
        const formData = new FormData(mainForm);
        localStorage.setItem('formData', JSON.stringify(Object.fromEntries(formData.entries())));

        // Create a hidden iframe for form submission
        const iframe = document.createElement('iframe');
        iframe.name = 'form-submission-iframe';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        // Set form target to iframe
        mainForm.target = 'form-submission-iframe';

        // Seed the calendar's storage and start loading slots now, so the
        // thank-you page has them (or nearly) by the time it mounts.
        const isPro = selectedPipeline === 'Professional';

        // This quiz has four contact pages (7, 701, 702, 703). Read the one
        // actually on screen, not whichever comes first in the DOM.
        const contactPage =
            document.querySelector('.quiz-page[style="display: block;"]') ||
            [...document.querySelectorAll('.quiz-page')].find(
                (page) => page.querySelector('input[type=email]')?.value.trim()
            );

        const calendarEmail =
            contactPage?.querySelector('input[type=email]')?.value.trim() ||
            emailField?.value.trim() ||
            '';

        // No single name field - collect first/last from the page in view.
        const nameFields = [
            ...(contactPage?.querySelectorAll('input[name*="name" i]') ?? []),
        ].filter((field) => field.type !== 'hidden' && field.value.trim());

        const nameParts =
            nameFields.length > 1
                ? nameFields.map((field) => field.value.trim())
                : (nameFields[0]?.value.trim() ?? '').split(/\s+/);

        let slotsPromise = Promise.resolve();

        if (window.RepsCalendar) {
            RepsCalendar.saveForm({
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' '),
                email: calendarEmail,
                phone:
                    contactPage?.querySelector('input[type=tel]')?.value.trim() ||
                    phoneField?.value ||
                    '',
                role: isPro ? 'pro' : 'consumer',
            });

            // Fire and forget - we only wait for it just before redirecting.
            slotsPromise = RepsCalendar.loadTimeslots(calendarEmail, {
                pro: isPro,
            }).catch((error) => console.error('[quiz] loadTimeslots', error));
        } else {
            console.warn('[quiz] RepsCalendar not loaded - skipping slot preload');
        }

        iframe.onload = function () {
            setTimeout(async () => {
                document.body.removeChild(iframe);

                const tyPage = document.getElementById('flexible-closet-quiz-form').getAttribute("data-thank-you-page");

                const encodedEmail = btoa(calendarEmail);
                const encodedPipeline = btoa(selectedPipeline);
                const requestUuid = localStorage.getItem('request_uuid') ?? window.requestUuid;

                // Don't let a slow or dead availability call strand them here.
                await Promise.race([
                    slotsPromise,
                    new Promise((resolve) => setTimeout(resolve, 5000)),
                ]);

                window.location.href = `${tyPage}?elfg=${encodedEmail}&ppln=${encodedPipeline}&rid=${requestUuid}`;
            }, 1000);
        };


        // If pro checkbox is unchecked, value should be 'Homeowner'
        const personalCheckbox = document.querySelector('input[name="personal-info"][type="checkbox"][value="Professional"]');
        if (personalCheckbox && !personalCheckbox.checked) {
            const existing = mainForm.querySelectorAll('input[type="hidden"][name="personal-info"]');
            existing.forEach(n => n.remove());

            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'personal-info';
            hidden.value = 'Homeowner';
            mainForm.appendChild(hidden);
        }


        // Submit the form
        mainForm.submit();
    }

    function getRecipeLink(formData) {

        // Only continue if has walk-in or reach-in closet type
        const closetType = formData.get('closet_type') || '';
        if (!closetType.toLowerCase().includes('walk-in') && !closetType.toLowerCase().includes('reach-in')) {
            console.log(" no walk-in or reach-in");
            return '';
        }

        // get wall measurements
        let wallA = formData.get('q24_wallA');
        let wallB = formData.get('q25_wallB');
        let wallC = formData.get('q26_wallC');
        let wallD = formData.get('q27_wallD');
        let wallE = formData.get('q28_wallE');
        let entrance = formData.get('q29_entrance');

        // check if all wall measurements are present
        if (wallA === '' || wallB === '' || wallC === '' || wallD === '' || wallE === '' || entrance === '') {
            console.log(" missing wall measurement");
            return '';
        }

        // make sure they are numbers, if not change them into numbers or return null
        wallA = changeToValidInches(wallA);
        wallB = changeToValidInches(wallB);
        wallC = changeToValidInches(wallC);
        wallD = changeToValidInches(wallD);
        wallE = changeToValidInches(wallE);
        entrance = changeToValidInches(entrance);

        // if any wall measurement is null, return empty
        if (wallA === null || wallB === null || wallC === null || wallD === null || wallE === null || entrance === null) {
            console.log(" invalid wall measurement");
            return '';
        }

        // validate minimum and max wall & door measurements
        if (wallA < 1 || wallC < 30 || wallB < 17 || wallD < 17 || wallE < 1 || entrance < 8 || entrance > 200) {
            console.log(" wall measurement out of range");
            return '';
        }

        // wall C should equal wall A + wall E + entrance
        if (wallC !== wallA + wallE + entrance) {
            console.log(" wall C not equal to wall A + wall E + entrance");
            return '';
        }

        // wall D should equal wall B
        if (wallD !== wallB) {
            console.log(" wall D not equal to wall B");
            return '';
        }

        // All validations passed, return recipe link
        return ` https://17squares.com/modularClosets/?recipeName=AltoNoDoor&WallA=${wallB}&WallB=${wallC}&WallC=${wallD}&WallD=${wallE}&WallE=${wallA}&CeilingHeight=96`;
    }

    function changeToValidInches(value) {
        if (value == null) return null;

        // If it's already a number, treat as inches
        if (typeof value === 'number' && !Number.isNaN(value)) return value;

        if (typeof value === 'string') {
            const trimmed = value.trim();

            // Pure numeric string => inches
            if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
                return Number(trimmed);
            }

            // Feet + inches: feet must be integer; inches can be integer OR decimal
            // e.g., 5' 7", 5'7.5"
            const feetInches = trimmed.match(/^\s*(\d+)\s*'\s*(\d+(?:\.\d+)?)\s*"\s*$/);
            if (feetInches) {
                const feet = parseInt(feetInches[1], 10);
                const inches = parseFloat(feetInches[2]);
                return feet * 12 + inches;
            }

            // Feet-only (allow decimals), e.g. 3.5'
            const feetOnly = trimmed.match(/^\s*(\d+(?:\.\d+)?)\s*'\s*$/);
            if (feetOnly) {
                const feet = parseFloat(feetOnly[1]);
                return feet * 12;
            }

            // Inches-only (allow decimals), e.g. 11.5"
            const inchesOnly = trimmed.match(/^\s*(\d+(?:\.\d+)?)\s*"\s*$/);
            if (inchesOnly) {
                return parseFloat(inchesOnly[1]);
            }
        }

        // Anything else is invalid
        return null;
    }

    // =======================================================================================
    // When select multiple rooms, show only the room options that were selected on next step
    // =======================================================================================
    function filterRoomTypeFields(step) {
        if (!step.querySelector('.more-than-one-room-measurements')) return;

        // Get all checked room values from room selection page checkbox group
        const checkedRooms = Array.from(
            document.querySelectorAll('input[name="room_type"]:checked')
        ).map(cb => cb.value.toLowerCase().trim());

        // For each textarea block on this page, show/hide based on label match
        step.querySelectorAll('.form-field').forEach(field => {
            const labelEl = field.querySelector('.field-label p, .field-label');
            if (!labelEl) return;

            const labelText = labelEl.textContent.toLowerCase().trim();
            const isMatch = checkedRooms.some(room => labelText.includes(room.toLowerCase()));
            field.style.display = isMatch ? '' : 'none';
        });

        // Always show the file upload field regardless
        step.querySelectorAll('.file-upload-field').forEach(f => f.style.display = '');

        // Even number of rooms → the upload spans the row.
        // Odd → it sits beside the last textarea.
        const visible = Array.from(
            step.querySelectorAll('.form-field:not(.file-upload-field)')
        ).filter(f => f.style.display !== 'none');

        step.querySelectorAll('.file-upload-field').forEach(f => {
            f.classList.toggle('span-full', visible.length % 2 === 0);
        });
    }


    // =========================================
    // Form Validation and Submission
    // =========================================

    /**
     * Validates all fields in the form including required fields and patterns
     * @returns {boolean} Whether the form is valid
     */
    function validateFields() {
        const currentPage = document.querySelector('.quiz-page[style="display: block;"]');
        if (!currentPage) return true;

        const fields = currentPage.querySelectorAll('input, textarea');
        let isValid = true;

        // Reset all error messages first
        currentPage.querySelectorAll('.error-message').forEach(error => {
            error.style.display = 'none';
        });

        fields.forEach(field => {
            let fieldValid = true;
            const formField = field.closest('.form-field,.flexible-cdq-room');
            const errorMessage = formField?.querySelector('.error-message');

            // Check required fields
            if (field.hasAttribute('required') && !field.value.trim()) {
                fieldValid = false;
            }

            // Check pattern validation
            if (fieldValid && field.hasAttribute('pattern')) {
                const pattern = new RegExp(field.getAttribute('pattern'));
                if (!pattern.test(field.value)) {
                    fieldValid = false;
                }
            }

            // Check email validation
            if (fieldValid && field.type === 'email') {
                const emailPattern = /^(?=.{1,64}@)[A-Za-z0-9!#$%&'*+/=?^_{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;;
                if (!emailPattern.test(field.value)) {
                    fieldValid = false;
                }
            }

            // Check phone validation
            if (fieldValid && field.type === 'tel') {
                const phonePattern = /^\+?[\d\s-()]{10,}$/;
                if (!phonePattern.test(field.value)) {
                    fieldValid = false;
                }
            }

            // Check radio groups
            if (fieldValid && field.type === 'radio' && field.hasAttribute('required')) {
                const radioGroup = field.name;
                const checkedRadio = currentPage.querySelector(`input[name="${radioGroup}"]:checked`);
                if (!checkedRadio) {
                    fieldValid = false;
                }
            }

            // Check checkbox groups
            if (fieldValid && field.type === 'checkbox' && field.hasAttribute('required')) {
                const checkboxGroup = field.name;
                const checkedCheckboxes = currentPage.querySelectorAll(`input[name="${checkboxGroup}"]:checked`);
                if (checkedCheckboxes.length === 0) {
                    fieldValid = false;
                }
            }
            // Show error message if field is invalid
            if (!fieldValid) {
                isValid = false;
                if (errorMessage) {
                    errorMessage.style.display = 'block';
                }
            }
        });

        return isValid;
    }


    /**
     * Adds UTM and tracking parameters to the form
     */
    function addTrackingParameters() {
        // Get UTM parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = {
            utm_source: urlParams.get('utm_source'),
            utm_medium: urlParams.get('utm_medium'),
            utm_campaign: urlParams.get('utm_campaign'),
            utm_term: urlParams.get('utm_term'),
            utm_content: urlParams.get('utm_content'),
            utm_referlclick: urlParams.get('utm_referlclick'),
            utm_adgroup: urlParams.get('utm_adgroup'),
            utm_ad: urlParams.get('utm_ad'),
            utm_platform: urlParams.get('utm_platform')
        };

        var deviceType = "desktop";
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            deviceType = "mobile";
        }

        // Get tracking IDs from local storage
        const trackingIds = {
            gclid: localStorage.getItem('gclid'),
            fbclid: localStorage.getItem('fbclid'),
            ttclid: localStorage.getItem('ttclid'),
            landing_page: localStorage.getItem('landingPage'),
            device: deviceType
        };

        // if utm_adgroup is not present, try to get from landing page parameter
        if (!utmParams.utm_adgroup && trackingIds.landing_page && trackingIds.landing_page.includes('adgroup=')) {
            utmParams.utm_adgroup = trackingIds.landing_page.split('adgroup=')[1].split('&')[0];
        }

        // Add all parameters to hidden fields
        [...Object.entries(utmParams), ...Object.entries(trackingIds)].forEach(([key, value]) => {
            console.log(" key,value:", key, value);
            if (value) {
                const hiddenField = document.querySelector(`input.${key}`);
                if (hiddenField) {
                    hiddenField.value = value;
                }
            }
        });
    }

    /**
     * Submit the closet design quiz
     */
    function submitClosetDesignQuiz() {
        const currentPage = document.querySelector('.quiz-page[style="display: block;"]');
        const continueButton = currentPage?.querySelector('.btn-type-submit');
        if (continueButton) {
            continueButton.style.display = 'none';
            const spinner = continueButton.parentElement.querySelector('.flexible-cdq-spinner');
            if (spinner) spinner.style.display = '';
        }

        if (document.querySelector('#recaptcha-score') != null) {
            getRecaptchaAndSubmit(copyFieldsAndSubmitForm, currentPage);
        } else {
            copyFieldsAndSubmitForm()
        }
    }

    // =========================================
    // Event Listeners
    // =========================================

    // Initialize form
    initializePages();
    initProgressBar();

    // Bind button-state listeners to every page up front
    pages.forEach(page => bindButtonStateListeners(page));

    // Run initial button state for the first visible page
    const firstPage = document.querySelector('.quiz-page[style="display: block;"]');
    if (firstPage) {
        updateButtonState(firstPage);
        updateProgressBar(firstPage);

        const prevArrow = document.querySelector('.prev-arrow');
        if (prevArrow) prevArrow.style.display = 'none';
    }

    // Radio button changes
    document.querySelectorAll('.quiz-page input[type="radio"]').forEach(radio => {
        radio.addEventListener('change', function (e) {
            const optionLabel = this.closest('.option-label');
            const optionGroup = optionLabel.closest('.option-group');
            handleOptionSelection(optionLabel, optionGroup);

            if (optionLabel.classList.contains('changed')) {
                return;
            }
            optionLabel.classList.add('changed');

            // Only auto-advance for no_button pages (radio list that advances on selection)
            const page = this.closest('.quiz-page');
            const btn = page?.querySelector('.flexible-cdq-continue');
            const isNoButton = !btn;
            if (!isNoButton) return;

            if (!validateFields()) return;
            handleBranching(this.dataset.targetStep, this.dataset.targetUrl);
        });
    });

    // Option click — only applies to radio inputs (not checkboxes, which support multi-select)
    document.querySelectorAll('.quiz-page .option-label').forEach(label => {
        label.addEventListener('click', function () {
            const input = label.querySelector('input');
            if (!input || input.type === 'checkbox') return;

            if (this.classList.contains('changed')) {
                return;
            }
            if (this.classList.contains('selected') || input.checked) {
                this.classList.add('changed');

                // Only auto-advance for no_button pages
                const page = this.closest('.quiz-page');
                const btn = page?.querySelector('.flexible-cdq-continue');
                if (btn) return;

                if (!validateFields()) return;
                handleBranching(input.dataset.targetStep, input.dataset.targetUrl);
            }
        });
    });

    // Checkbox changes — only update selected state, never auto-advance
    document.querySelectorAll('.quiz-page input[type="checkbox"]').forEach(checkbox => {
        checkbox.addEventListener('change', function () {
            const optionLabel = this.closest('.option-label');
            if (optionLabel) {
                if (this.checked) {
                    optionLabel.classList.add('selected');
                } else {
                    optionLabel.classList.remove('selected');
                }
            }
        });
    });

    // file upload - thumbnails with remove + add
    document.querySelectorAll('.file-upload-field').forEach((field) => {
        const uploadLabel = field.querySelector('.file-upload-label-area');
        const fileInput = field.querySelector('.file-input');
        const filePreviewArea = field.querySelector('.file-preview-wrapper');
        const fileAmount = field.querySelector('.file-amount');
        const fileAmountS = field.querySelector('.file-amount-s');
        const thumbnailsRow = field.querySelector('.thumbnails-row');

        const MAX_FILES = 3;
        let fileArray = []; // each entry: { file, inputEls: [] }

        // Renumber every hidden S3-key input to match current visual order in fileArray.
        // Safe to call any time — files still uploading (inputEls === []) are simply skipped
        // until their input exists, then get numbered on the next call.
        function renumberInputs() {
            fileArray.forEach((entry, index) => {
                entry.inputEls.forEach(el => {
                    el.name = `${fileInput.name}_s3_key_${index}`;
                });
            });
        }

        function renderThumbnails() {
            thumbnailsRow.innerHTML = '';

            fileArray.forEach((entry, index) => {
                const thumb = document.createElement('div');
                thumb.className = 'thumbnail-item';

                const img = document.createElement('img');
                img.src = URL.createObjectURL(entry.file);
                img.onload = () => URL.revokeObjectURL(img.src);

                const removeBtn = document.createElement('button');
                removeBtn.className = 'thumbnail-remove';
                removeBtn.type = 'button';
                removeBtn.setAttribute('aria-label', 'Remove image');
                removeBtn.innerHTML = '&times;';
                removeBtn.addEventListener('click', () => {
                    entry.inputEls.forEach(el => el.remove());
                    fileArray.splice(index, 1);
                    renumberInputs();
                    updatePreview();
                });

                thumb.appendChild(img);
                thumb.appendChild(removeBtn);
                thumbnailsRow.appendChild(thumb);
            });

            if (fileArray.length < MAX_FILES) {
                const addTile = document.createElement('div');
                addTile.className = 'thumbnail-item thumbnail-add';
                addTile.innerHTML = '<span>+</span>';
                addTile.addEventListener('click', () => fileInput.click());
                thumbnailsRow.appendChild(addTile);
            }
        }

        function updatePreview() {
            if (fileArray.length > 0) {
                uploadLabel.style.display = 'none';
                filePreviewArea.style.display = 'flex';
                fileAmount.innerText = fileArray.length;
                fileAmountS.style.display = fileArray.length > 1 ? "contents" : "none";
            } else {
                uploadLabel.style.display = '';
                filePreviewArea.style.display = 'none';
            }
            renderThumbnails();
        }

        fileInput.addEventListener('change', (event) => {
            const newFiles = Array.from(event.target.files);
            const availableSlots = MAX_FILES - fileArray.length;

            if (availableSlots <= 0) {
                fileInput.value = '';
                return;
            }

            const filesToAdd = newFiles.slice(0, availableSlots);

            filesToAdd.forEach(file => {
                const entry = { file, inputEls: [] };
                fileArray.push(entry);

                if (window.uploadLambdaFile) {
                    window.uploadLambdaFile(file, field, fileInput.name).then(inputEls => {
                        if (inputEls) entry.inputEls = inputEls;
                        renumberInputs(); // re-sync names once this upload's input exists
                    });
                }
            });

            fileInput.value = '';
            updatePreview();
        });

    })
    // Show more / show less for checkbox groups
    document.querySelectorAll('.checkbox-show-more').forEach(btn => {
        btn.addEventListener('click', function () {
            const optionGroup = this.closest('.option-group');
            const isExpanded = this.getAttribute('aria-expanded') === 'true';

            if (isExpanded) {
                optionGroup.querySelectorAll('.checkbox-overflow-item').forEach(item => {
                    item.classList.remove('visible');
                });
                this.querySelector('.checkbox-show-more-text').textContent = this.dataset.preview;
                this.querySelector('svg').style.transform = '';
                this.setAttribute('aria-expanded', 'false');
            } else {
                optionGroup.querySelectorAll('.checkbox-overflow-item').forEach(item => {
                    item.classList.add('visible');
                });
                this.querySelector('.checkbox-show-more-text').textContent = 'See less options';
                this.querySelector('svg').style.transform = 'rotate(180deg)';
                this.setAttribute('aria-expanded', 'true');
            }
        });
    });

    // Quiz option buttons
    document.querySelectorAll('.quiz-option-button').forEach(button => {
        button.addEventListener('click', function () {
            const optionGroup = this.closest('.option-group');
            handleOptionSelection(this, optionGroup);
            if (!validateFields()) return;
            handleBranching(this.dataset.targetStep, this.dataset.targetUrl);
        });
    });

    // Professional/Retail option
    document.addEventListener('change', (e) => {
        if (!e.target.matches('input[name="personal-info"]')) return;

        const radio = document.querySelector('input[name="personal-info"][type="radio"]:checked');
        const proCheckbox = document.querySelector('input[name="personal-info"][type="checkbox"][value="Professional"]');

        selectedPipeline = radio
            ? (radio.value === 'Professional' ? 'Professional' : 'Retail')
            : (proCheckbox?.checked ? 'Professional' : 'Retail');

        console.log('selected pipeline:', selectedPipeline);
    });

    // Continue buttons (mandatory, optional, submit) and nav arrows
    document.querySelectorAll('.flexible-cdq-continue,.prev-arrow').forEach(button => {
        button.addEventListener('click', function () {
            const currentStep = this.closest('.quiz-page') || document.querySelector('.quiz-page[style="display: block;"]');

            if (this.classList.contains('prev-arrow')) {
                navigateToStep(currentStep, null, true);
            } else if (this.classList.contains('btn-type-submit')) {
                if (!validateFields()) return;
                submitClosetDesignQuiz();
            } else {
                if (!validateFields()) return;
                const checkedCheckboxes = currentStep.querySelectorAll('input[type="checkbox"].option-input:checked');
                let checkboxTargetStep = null;
                if (checkedCheckboxes.length > 0) {
                    if (checkedCheckboxes.length > 1) {
                        const groupTarget = checkedCheckboxes[0]
                            .closest('.option-group')
                            ?.dataset.groupTargetStep;
                        checkboxTargetStep = groupTarget || null;
                    }
                    if (!checkboxTargetStep) {
                        for (const cb of checkedCheckboxes) {
                            if (cb.dataset.targetStep) {
                                checkboxTargetStep = cb.dataset.targetStep;
                                break;
                            }
                        }
                    }
                }
                const targetStep = checkboxTargetStep || this.dataset.skipToStep;
                navigateToStep(currentStep, targetStep);
            }
        });
    });

    // GA4 - Quiz interaction tracking
    document.addEventListener('click', function (e) {
        const input = e.target.closest('input, textarea');
        const button = e.target.closest('button, .btn');
        const target = input || button;

        if (!target || !target.id || target.dataset.ga4Tracked) return;

        const page = target.closest('[data-page]')?.dataset.page;

        if (!page) return;

        target.dataset.ga4Tracked = 'true';

        window.dataLayer.push({
            event: 'quiz_field_click',
            field_id: target.id,
            quiz_page: Number(String(page)[0])
        });
    });

    // GA4 - Autofill detection on contact page
    document.addEventListener('animationstart', function (e) {
        if (e.animationName !== 'cdqAutoFillStart') return;

        const target = e.target;
        if (!target || !target.id || target.dataset.ga4Tracked) return;
        if (!target.value || target.value.trim() === '') return;

        const page = target.closest('[data-page]')?.dataset.page;
        if (!page) return;

        target.dataset.ga4Tracked = 'true';

        window.dataLayer.push({
            event: 'quiz_field_click',
            field_id: target.id,
            quiz_page: Number(String(page)[0])
        });
    });
});
